import {
  EFileUploadStatus,
  type IIntegrationFetchEntry,
  type IUploadProgressEntry,
} from "@frontend-shared/types";
import { stampTransferStartTimes } from "@frontend-shared/utils";
import type { PropsWithChildren, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { TransferContext } from "@/contexts/TransferContext";
import { useAuth } from "@/hooks/use-auth";
import { useMeetingUploads } from "@/hooks/use-meeting-uploads";
import { useValidatedMeetingUpload } from "@/hooks/use-validated-meeting-upload";
import { ChatContext } from "@/pages/chat/context/ChatContext";
import type { IAttachment } from "@/pages/chat/types";
import { IntegrationHandoffBridge } from "@/pages/uploads/components/IntegrationHandoffBridge";
import type { IUploadFileParams } from "@/pages/uploads/types";

// NOTE: NSAI-7067 — sits directly under `MeetingUploadProvider` and completes
// what NSAI-5141 started: that provider lifted the upload store above the
// routes, this lifts the connector fetch that feeds it, so a file picked from
// Google Drive keeps downloading — and then uploads — while the user is on
// another page. Web twin of mobile's TransferProvider (NSAI-5241).
//
// NOTE: It owns the attachment store the Uploads page's picker writes into and
// mounts the hand-off inside it, beside — not around — the app. See
// `pickerChatContext` in TransferContext for why that store is not a root
// ChatContextProvider here.
const TransferProvider = ({ children }: PropsWithChildren): ReactNode => {
  const { uploads, cancelUpload, removeUpload } = useMeetingUploads();
  const uploadValidatedFile = useValidatedMeetingUpload();
  const { user, currentProjectUuid } = useAuth();

  const [attachments, setAttachments] = useState<IAttachment[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);

  // NOTE: NSAI-5132 — files still being pulled out of a third-party app. Not
  // uploads yet (no S3 traffic has happened), but the phase the user waits
  // through, so they share one list with the uploads that follow them.
  const [fetchingFiles, setFetchingFiles] = useState<IIntegrationFetchEntry[]>(
    [],
  );
  const fetchStartTimesRef = useRef(new Map<string, number>());

  const handleFetchingFilesChange = useCallback(
    (entries: IIntegrationFetchEntry[]): void => {
      setFetchingFiles(
        stampTransferStartTimes({
          entries,
          startTimes: fetchStartTimesRef.current,
          now: Date.now(),
        }),
      );
    },
    [],
  );

  const uploadQueuedListenersRef = useRef<Set<() => void>>(new Set());

  const registerUploadQueuedListener = useCallback(
    (listener: () => void): (() => void) => {
      const listeners = uploadQueuedListenersRef.current;
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },
    [],
  );

  const handleUploadQueued = useCallback((): void => {
    uploadQueuedListenersRef.current.forEach((listener) => listener());
  }, []);

  // NOTE: A fetch belongs to the (user, project) it was started in, exactly as
  // an upload does (see MeetingUploadProvider): the hand-off's presign picks
  // up whatever `X-Project-Uuid` the api client resolves at request time, so a
  // file that finishes downloading after a switch would be uploaded into the
  // project the user landed on. Abort and drop every fetch on a switch.
  //
  // NOTE: State, not a ref, and read during render on purpose — mobile's
  // provider records why: React runs a fiber's effects in declaration order
  // within one commit, so a ref advanced by the purge effect would already
  // match by the time the hand-off effect asked "is this the transition
  // render?", and the guard would never fire.
  const sessionScope =
    user?.uuid && currentProjectUuid
      ? `${user.uuid}:${currentProjectUuid}`
      : null;
  const [previousScope, setPreviousScope] = useState<string | null>(
    sessionScope,
  );
  const isScopeTransition = previousScope !== sessionScope;

  useEffect(() => {
    if (previousScope !== null && isScopeTransition) {
      for (const attachment of attachments) {
        attachment.cancel?.();
      }
      setAttachments([]);
      setIsDownloading(false);
      setFetchingFiles([]);
    }
    setPreviousScope(sessionScope);
  }, [attachments, isScopeTransition, previousScope, sessionScope]);

  // NOTE: A READY file seen on the transition render is the old scope's —
  // effects still see that render's attachment snapshot after the purge above
  // schedules its removal.
  const uploadFileFromIntegration = useCallback(
    (props: IUploadFileParams): boolean => {
      if (isScopeTransition) {
        return false;
      }

      return uploadValidatedFile(props);
    },
    [isScopeTransition, uploadValidatedFile],
  );

  // NOTE: The provider download is an XHR owned by this document, so a reload
  // or tab-close really does kill it — warn before that happens, as
  // MeetingUploadProvider does for the S3 PUT. In-app navigation is not
  // guarded at all any more: that is the point of this provider.
  const hasActiveFetches = fetchingFiles.length > 0;

  useEffect(() => {
    if (!hasActiveFetches) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      Object.assign(event, { returnValue: "" });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasActiveFetches]);

  // NOTE: Fetch rows go last: a file leaves this list at the same moment it
  // enters `uploads` at the end, so its row keeps its place instead of jumping
  // across the list mid-transfer.
  const transfers = useMemo<IUploadProgressEntry[]>(
    () => [...uploads, ...fetchingFiles],
    [uploads, fetchingFiles],
  );

  const cancelTransfer = useCallback(
    (entry: IUploadProgressEntry): void => {
      if (entry.status === EFileUploadStatus.FETCHING) {
        fetchingFiles
          .find((candidate) => candidate.id === entry.id)
          ?.cancel?.();
        return;
      }

      // NOTE: CONFIRMING belongs here too: `confirm-upload` is the request that
      // creates the meeting, and MeetingUploadProvider passes it the same abort
      // signal precisely so this phase can be stopped. Dropping the row
      // without aborting would hide an upload that then succeeds behind the
      // user's back.
      if (
        entry.status === EFileUploadStatus.UPLOADING ||
        entry.status === EFileUploadStatus.CONFIRMING
      ) {
        cancelUpload(entry.id);
        return;
      }

      removeUpload(entry.id);
    },
    [cancelUpload, fetchingFiles, removeUpload],
  );

  const pickerChatContext = useMemo(
    () => ({ attachments, setAttachments, isDownloading, setIsDownloading }),
    [attachments, isDownloading],
  );

  const value = useMemo(
    () => ({
      transfers,
      cancelTransfer,
      registerUploadQueuedListener,
      pickerChatContext,
    }),
    [
      transfers,
      cancelTransfer,
      registerUploadQueuedListener,
      pickerChatContext,
    ],
  );

  return (
    <TransferContext.Provider value={value}>
      <ChatContext.Provider value={pickerChatContext}>
        <IntegrationHandoffBridge
          uploadFile={uploadFileFromIntegration}
          onFetchingFilesChange={handleFetchingFilesChange}
          onUploadQueued={handleUploadQueued}
        />
      </ChatContext.Provider>
      {children}
    </TransferContext.Provider>
  );
};

export default TransferProvider;
